import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

// A number drawn like a mechanical counter: every digit is a 0–9 drum that rolls to its
// new position when the value changes, the units first and each place to the left a
// beat later, and the whole number flashes green or red for the direction it moved.
// The first render lands without rolling; reduced-motion users get the plain swap.
export function RollingNumber({
  value,
  format = (n) => n.toLocaleString(),
  className,
}: {
  value: number;
  format?: (n: number) => string;
  className?: string;
}) {
  const text = format(value);
  const previous = useRef(value);
  const [trend, setTrend] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    if (value === previous.current) return;
    setTrend(value > previous.current ? 'up' : 'down');
    previous.current = value;
    const timer = setTimeout(() => setTrend(null), 900);
    return () => clearTimeout(timer);
  }, [value]);

  const chars = [...text];
  return (
    <span
      className={cn(
        'inline-flex tabular-nums transition-colors duration-700',
        className,
        trend === 'up' && 'text-green-600',
        trend === 'down' && 'text-red-600',
      )}
    >
      <span className="sr-only">{text}</span>
      <span aria-hidden className="inline-flex">
        {chars.map((char, index) => {
          // Keyed by place from the right, so the units drum stays the units drum
          // when the number grows a digit.
          const place = chars.length - 1 - index;
          return /\d/.test(char) ? (
            <Drum key={`d${place}`} digit={Number(char)} place={place} />
          ) : (
            <span key={`c${place}`} className="whitespace-pre">
              {char}
            </span>
          );
        })}
      </span>
    </span>
  );
}

function Drum({ digit, place }: { digit: number; place: number }) {
  return (
    <span className="inline-block h-[1lh] overflow-hidden">
      <span
        className="flex flex-col transition-transform duration-700 ease-[cubic-bezier(0.25,1.35,0.4,1)] motion-reduce:transition-none"
        style={{
          transform: `translateY(-${digit * 10}%)`,
          transitionDelay: `${Math.min(place, 6) * 45}ms`,
        }}
      >
        {DIGITS.map((d) => (
          <span key={d} className="h-[1lh]">
            {d}
          </span>
        ))}
      </span>
    </span>
  );
}
